import { ApiClient } from '@/lib/axios/client';
import type {
  Profile,
  ProfileListing,
  CollectProfileRequest,
  ProfileAnalysis,
  CompareProfilesRequest,
  ProfileComparison,
  ProfilingConfig,
  RuntimeStats,
  ListProfilesParams,
} from './types';

export class ProfilingApi {
  /**
   * List all profiles with optional filtering
   */
  static async listProfiles(params?: ListProfilesParams): Promise<ProfileListing> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.offset) queryParams.set('offset', params.offset.toString());
    if (params?.profile_type) queryParams.set('profile_type', params.profile_type);
    if (params?.from_date) queryParams.set('from_date', params.from_date);
    if (params?.to_date) queryParams.set('to_date', params.to_date);

    const query = queryParams.toString();
    return await ApiClient.get<ProfileListing>(
      `/api/v1/admin/profiles${query ? `?${query}` : ''}`
    );
  }

  /**
   * Get a single profile by ID
   */
  static async getProfile(profileId: string): Promise<Profile> {
    return await ApiClient.get<Profile>(`/api/v1/admin/profiles/${profileId}`);
  }

  /**
   * Collect a new profile
   */
  static async collectProfile(request: CollectProfileRequest): Promise<Profile> {
    return await ApiClient.post<Profile>('/api/v1/admin/profiles', request);
  }

  /**
   * Delete a profile
   */
  static async deleteProfile(profileId: string): Promise<void> {
    await ApiClient.delete(`/api/v1/admin/profiles/${profileId}`);
  }

  /**
   * Analyze a profile
   */
  static async analyzeProfile(profileId: string): Promise<ProfileAnalysis> {
    return await ApiClient.get<ProfileAnalysis>(
      `/api/v1/admin/profiles/${profileId}/analyze`
    );
  }

  /**
   * Compare two profiles
   */
  static async compareProfiles(request: CompareProfilesRequest): Promise<ProfileComparison> {
    return await ApiClient.post<ProfileComparison>(
      '/api/v1/admin/profiles/compare',
      request
    );
  }

  /**
   * Get profiling configuration
   */
  static async getConfig(): Promise<ProfilingConfig> {
    return await ApiClient.get<ProfilingConfig>('/api/v1/admin/profiles/config');
  }

  /**
   * Update profiling configuration
   */
  static async updateConfig(config: Partial<ProfilingConfig>): Promise<ProfilingConfig> {
    return await ApiClient.patch<ProfilingConfig>(
      '/api/v1/admin/profiles/config',
      config
    );
  }

  /**
   * Get current runtime statistics
   */
  static async getRuntimeStats(): Promise<RuntimeStats> {
    return await ApiClient.get<RuntimeStats>('/api/v1/admin/profiles/runtime-stats');
  }
}
